"use client"

import { useMemo, useState } from "react"
import { CheckCircle2, Info, KeyRound, Link2 } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/common/components/ui/dialog"
import { Input } from "@/common/components/ui/input"
import { Label } from "@/common/components/ui/label"
import { Progress } from "@/common/components/ui/progress"
import WizardStepper from "@/common/components/wizard/wizard-stepper"
import { BAUSTEINE } from "@/modules/lernpfad/data/bausteine"
import { SKILL_BY_ID } from "@/modules/lernpfad/data/skills"
import {
    importLineDelayMs,
    moodleCmid,
    REVIEW_FLAGGED_BAUSTEINE,
} from "@/modules/lernpfad/data/connectors"
import type { Localizer } from "@/modules/lernpfad/i18n/localize"
import SyncLog, { type SyncLogLine } from "./sync-log"

/**
 * The connect-an-LMS wizard — flow 1 of the wow demo.
 *
 * Entirely simulated (no network), but every detail is lifted from the real
 * integration design in docs/lms-integration.md: the token model is Moodle's
 * actual web-services shape, the discovery step lists the REAL function names
 * the adapter would call, and the "catalogue" that streams in is the demo's own
 * Bausteine wearing Moodle-shaped cmids. When a real MoodleAdapter exists it
 * replaces the timers; the narration stays true.
 */

const POPULATION = 1960
/** Matches the seeded transcript scale so the HR numbers agree later. */
const SYNCED_COMPLETIONS = 14382

interface ConnectWizardProps {
    open: boolean
    onClose: () => void
    onConnected: (syncedCourses: number) => void
    localizer: Localizer
}

type Phase = "access" | "discovery" | "catalog" | "transcript" | "done"

const PHASE_INDEX: Record<Phase, number> = {
    access: 1,
    discovery: 2,
    catalog: 3,
    transcript: 4,
    done: 5,
}

export default function ConnectWizard({ open, onClose, onConnected, localizer }: ConnectWizardProps) {
    const { t } = localizer
    const [phase, setPhase] = useState<Phase>("access")
    const [phaseDone, setPhaseDone] = useState(false)
    const [progress, setProgress] = useState(0)

    const discoveryLines: SyncLogLine[] = useMemo(
        () => [
            { id: "d1", text: t("wiz.discovery.1") },
            { id: "d2", text: t("wiz.discovery.2"), detail: "GET" },
            { id: "d3", text: t("wiz.discovery.3"), detail: "GET" },
            { id: "d4", text: t("wiz.discovery.4"), detail: "GET" },
            { id: "d5", text: t("wiz.discovery.5") },
            { id: "d6", text: t("wiz.discovery.6") },
        ],
        [t],
    )

    const catalogLines: SyncLogLine[] = useMemo(
        () =>
            BAUSTEINE.map((baustein) => {
                const flagged = REVIEW_FLAGGED_BAUSTEINE.has(baustein.id)
                const skills = baustein.teaches
                    .map((entry) =>
                        localizer.skill(entry.skillId, SKILL_BY_ID.get(entry.skillId)?.label ?? entry.skillId),
                    )
                    .slice(0, 2)
                    .join(", ")
                return {
                    id: baustein.id,
                    text: `cmid ${moodleCmid(baustein.id)} · ${localizer.baustein(baustein).title}`,
                    detail: flagged ? t("wiz.catalog.review") : `→ ${skills}`,
                    flagged,
                }
            }),
        [localizer, t],
    )

    const reviewCount = REVIEW_FLAGGED_BAUSTEINE.size
    const mappedCount = BAUSTEINE.length - reviewCount

    const advance = (next: Phase) => {
        setPhase(next)
        setPhaseDone(false)
        setProgress(0)
        if (next === "transcript") {
            // Deterministic ramp: 24 ticks of ~140 ms ≈ 3.4 s over "1,960 people".
            let tick = 0
            const timer = setInterval(() => {
                tick += 1
                setProgress(Math.min(100, Math.round((tick / 24) * 100)))
                if (tick >= 24) {
                    clearInterval(timer)
                    setPhaseDone(true)
                }
            }, 140)
        }
    }

    const finish = () => {
        onConnected(BAUSTEINE.length)
        onClose()
        // Reset for the next run of the demo.
        setPhase("access")
        setPhaseDone(false)
        setProgress(0)
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t("wiz.title")}</DialogTitle>
                </DialogHeader>

                <WizardStepper
                    steps={[
                        t("wiz.step.access"),
                        t("wiz.step.discovery"),
                        t("wiz.step.catalog"),
                        t("wiz.step.transcript"),
                    ]}
                    current={PHASE_INDEX[phase]}
                    finalStep={t("wiz.final")}
                    className="mb-4"
                />

                {phase === "access" && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-1.5 text-xs">
                                <Link2 className="size-3" />
                                {t("wiz.url")}
                            </Label>
                            <Input defaultValue="https://learn.acme-bank.ch" readOnly />
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-1.5 text-xs">
                                <KeyRound className="size-3" />
                                {t("wiz.token")}
                            </Label>
                            <Input defaultValue="d41d8cd9••••••••••••••••••••7a2f" readOnly type="text" />
                            <p className="text-xs text-muted-foreground">{t("wiz.tokenHint")}</p>
                        </div>
                        <Button className="w-full" onClick={() => advance("discovery")}>
                            {t("wiz.start")}
                        </Button>
                    </div>
                )}

                {phase === "discovery" && (
                    <div className="space-y-4">
                        <SyncLog
                            lines={discoveryLines}
                            delayFor={(index) => 350 + importLineDelayMs(index) * 2}
                            onDone={() => setPhaseDone(true)}
                        />
                        <Button className="w-full" disabled={!phaseDone} onClick={() => advance("catalog")}>
                            {t("wiz.import")}
                        </Button>
                    </div>
                )}

                {phase === "catalog" && (
                    <div className="space-y-4">
                        <SyncLog
                            lines={catalogLines}
                            delayFor={importLineDelayMs}
                            onDone={() => setPhaseDone(true)}
                            maxHeight={220}
                        />
                        {phaseDone && (
                            <p className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                {t("wiz.catalog.done", {
                                    total: BAUSTEINE.length,
                                    mapped: mappedCount,
                                    review: reviewCount,
                                })}
                            </p>
                        )}
                        <Button className="w-full" disabled={!phaseDone} onClick={() => advance("transcript")}>
                            {t("wiz.sync")}
                        </Button>
                    </div>
                )}

                {phase === "transcript" && (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {t("wiz.transcript.running", { count: localizer.num(POPULATION) })}
                        </p>
                        <Progress value={progress} />
                        {phaseDone && (
                            <p className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                {t("wiz.transcript.found", { count: localizer.num(SYNCED_COMPLETIONS) })}
                            </p>
                        )}
                        <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                            <Info className="mt-0.5 size-3.5 shrink-0" />
                            {t("wiz.transcript.note")}
                        </div>
                        <Button className="w-full" disabled={!phaseDone} onClick={finish}>
                            {t("wiz.done")}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
